import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Popover,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { IconCheck } from '@tabler/icons-react';
import { ALL_USER_ROLES, type UserRole, type users } from '@techgitter/contracts';
import {
  DataTableCard,
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
  DataTableEmpty,
} from '../components/table';
import {
  fetchAllUsersForAdmin,
  updateUserRoles,
  updateUserAvatarColor,
  updateUserExtension,
} from '../services/usersAdminApi';
import { invalidateUsersCache } from '../services/usersCacheService';
import { AVATAR_PALETTE, resolveAvatarColor } from '../styles/avatarPalette';

interface RowState {
  saving: boolean;
  draft: UserRole[];
}

/** Per-row state for the inline 3CX extension field. */
interface ExtensionState {
  saving: boolean;
  draft: string;
}

/** cr001_extension is a MaxLength-10 digits-only string — mirror that here so
 *  the field can't produce a value the API will reject. */
const EXTENSION_MAX_LENGTH = 10;

const sanitizeExtension = (raw: string): string =>
  raw.replace(/\D/g, '').slice(0, EXTENSION_MAX_LENGTH);

/** Updated column: date only, no time. e.g. "11 Jun 2026". */
const formatUpdated = (dateString: string): string =>
  new Date(dateString).toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

/**
 * Admin Users page — manage role assignments, avatar colours and 3CX extensions.
 *
 * Renders all active users (technicians and non-technicians) with a chip-style
 * multi-select for each row's `roles`, a colour swatch, and an inline extension
 * field. Roles and colour save on change; the extension saves on blur/Enter.
 * All three PATCH the same route and surface inline error/success feedback.
 * Admin-only; gated by the parent AdminPage tab visibility.
 */
export const UsersAdminPage: React.FC = () => {
  const [allUsers, setAllUsers] = useState<users[]>([]);
  const [rowStates, setRowStates] = useState<Record<string, RowState>>({});
  const [extStates, setExtStates] = useState<Record<string, ExtensionState>>({});
  // Rows whose edit was abandoned with Escape — the blur that follows must not
  // save, and the DOM value it sees may still be the pre-Escape text.
  const extCancelledRef = useRef<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const refreshUsers = useCallback(async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      setLoadError(null);
      const data = await fetchAllUsersForAdmin(signal);
      const sorted = [...data].sort((a, b) =>
        (a.fullname || '').localeCompare(b.fullname || ''),
      );
      setAllUsers(sorted);
      setRowStates(
        Object.fromEntries(
          sorted.map((u) => [u.guid, { saving: false, draft: u.roles ?? [] }]),
        ),
      );
      setExtStates(
        Object.fromEntries(
          sorted.map((u) => [u.guid, { saving: false, draft: u.extension ?? '' }]),
        ),
      );
    } catch (e) {
      if ((e as DOMException)?.name === 'AbortError') return;
      setLoadError(e instanceof Error ? e.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    refreshUsers(controller.signal);
    return () => controller.abort();
  }, [refreshUsers]);

  const handleRolesChange = useCallback(
    async (user: users, nextRoles: UserRole[]) => {
      setRowStates((prev) => ({
        ...prev,
        [user.guid]: { saving: true, draft: nextRoles },
      }));
      try {
        await updateUserRoles(user.guid, nextRoles);
        setAllUsers((prev) =>
          prev.map((u) =>
            u.guid === user.guid ? { ...u, roles: nextRoles } : u,
          ),
        );
        setRowStates((prev) => ({
          ...prev,
          [user.guid]: { saving: false, draft: nextRoles },
        }));
        setSnackbar({
          open: true,
          message: `Updated roles for ${user.fullname}`,
          severity: 'success',
        });
      } catch (e) {
        // Roll back the draft on failure
        setRowStates((prev) => ({
          ...prev,
          [user.guid]: { saving: false, draft: user.roles ?? [] },
        }));
        setSnackbar({
          open: true,
          message:
            e instanceof Error
              ? e.message
              : `Failed to update roles for ${user.fullname}`,
          severity: 'error',
        });
      }
    },
    [],
  );

  const [colorAnchor, setColorAnchor] = useState<{
    el: HTMLElement;
    user: users;
  } | null>(null);

  const handleColorChange = useCallback(
    async (user: users, colorKey: string) => {
      setColorAnchor(null);
      const previous = user.avatarColor;
      // Optimistic update.
      setAllUsers((prev) =>
        prev.map((u) =>
          u.guid === user.guid ? { ...u, avatarColor: colorKey } : u,
        ),
      );
      try {
        await updateUserAvatarColor(user.guid, colorKey);
        // Drop the cached users list so the dashboard/topbar pick up the change.
        await invalidateUsersCache();
        setSnackbar({
          open: true,
          message: `Updated colour for ${user.fullname}`,
          severity: 'success',
        });
      } catch (e) {
        // Roll back on failure.
        setAllUsers((prev) =>
          prev.map((u) =>
            u.guid === user.guid ? { ...u, avatarColor: previous } : u,
          ),
        );
        setSnackbar({
          open: true,
          message:
            e instanceof Error
              ? e.message
              : `Failed to update colour for ${user.fullname}`,
          severity: 'error',
        });
      }
    },
    [],
  );

  const handleExtensionChange = useCallback((guid: string, raw: string) => {
    const draft = sanitizeExtension(raw);
    setExtStates((prev) => ({
      ...prev,
      [guid]: { saving: prev[guid]?.saving ?? false, draft },
    }));
  }, []);

  /** Escape abandons the edit: restore the stored value and skip the blur save. */
  const handleExtensionCancel = useCallback((user: users) => {
    extCancelledRef.current.add(user.guid);
    setExtStates((prev) => ({
      ...prev,
      [user.guid]: { saving: false, draft: user.extension ?? '' },
    }));
  }, []);

  /** Commit on blur (or Enter, which blurs). No-ops when nothing changed. */
  const handleExtensionCommit = useCallback(async (user: users, raw: string) => {
    if (extCancelledRef.current.delete(user.guid)) return;

    const stored = user.extension ?? '';
    const next = sanitizeExtension(raw);
    if (next === stored) return;

    setExtStates((prev) => ({ ...prev, [user.guid]: { saving: true, draft: next } }));
    try {
      await updateUserExtension(user.guid, next || null);
      setAllUsers((prev) =>
        prev.map((u) =>
          u.guid === user.guid ? { ...u, extension: next || undefined } : u,
        ),
      );
      setExtStates((prev) => ({ ...prev, [user.guid]: { saving: false, draft: next } }));
      // The users list is cached for the dashboard/topbar; drop it so callers
      // resolving an extension see the new mapping.
      await invalidateUsersCache();
      setSnackbar({
        open: true,
        message: next
          ? `Set extension ${next} for ${user.fullname}`
          : `Cleared extension for ${user.fullname}`,
        severity: 'success',
      });
    } catch (e) {
      // Roll back to the stored value — a 409 means someone else holds it.
      setExtStates((prev) => ({
        ...prev,
        [user.guid]: { saving: false, draft: stored },
      }));
      setSnackbar({
        open: true,
        message:
          e instanceof Error
            ? e.message
            : `Failed to update extension for ${user.fullname}`,
        severity: 'error',
      });
    }
  }, []);

  const roleOptions = useMemo(() => [...ALL_USER_ROLES], []);

  return (
    <Box sx={{ p: '12px 16px 24px' }}>
      <Typography
        sx={{ fontSize: 11, color: '#888888', lineHeight: 1.6, mb: 2, maxWidth: 720 }}
      >
        Manage role assignments, avatar colours and 3CX extensions for SM4RT
        users. Add or remove a role with the chip picker, or click a user's
        colour swatch to pick from the palette — changes save automatically. A
        dashed swatch is an auto-assigned colour; pick one to lock it in. The
        extension is the 3CX extension that user answers calls on; it attributes
        recorded calls to them, must be unique, and saves when you leave the
        field. Admin-only.
      </Typography>

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DataTableCard>
          <DataTable>
            <thead>
              <tr>
                <DataTableHeadCell>Colour</DataTableHeadCell>
                <DataTableHeadCell>Full Name</DataTableHeadCell>
                <DataTableHeadCell>Email</DataTableHeadCell>
                <DataTableHeadCell>Extension</DataTableHeadCell>
                <DataTableHeadCell>Roles</DataTableHeadCell>
                <DataTableHeadCell>Updated</DataTableHeadCell>
              </tr>
            </thead>
            <tbody>
              {allUsers.length === 0 ? (
                <DataTableEmpty colSpan={6}>No users found.</DataTableEmpty>
              ) : (
                allUsers.map((user) => {
                  const state = rowStates[user.guid] ?? {
                    saving: false,
                    draft: user.roles ?? [],
                  };
                  const extState = extStates[user.guid] ?? {
                    saving: false,
                    draft: user.extension ?? '',
                  };
                  const colour = resolveAvatarColor({
                    storedColor: user.avatarColor,
                    seed: user.id || user.guid,
                  });
                  const explicit = !!user.avatarColor;
                  return (
                    <DataTableRow key={user.guid}>
                      <DataTableCell style={{ width: 72 }}>
                        <Tooltip title={explicit ? `${colour.key} (chosen)` : `${colour.key} (auto)`}>
                          <button
                            type="button"
                            onClick={(e) => setColorAnchor({ el: e.currentTarget, user })}
                            aria-label={`Change avatar colour for ${user.fullname}`}
                            style={{
                              width: 22,
                              height: 22,
                              borderRadius: '50%',
                              background: colour.bg,
                              color: colour.fg,
                              border: explicit ? '0.5px solid rgba(0,0,0,0.15)' : '2px dashed rgba(0,0,0,0.25)',
                              cursor: 'pointer',
                              padding: 0,
                            }}
                          />
                        </Tooltip>
                      </DataTableCell>
                      <DataTableCell>{user.fullname}</DataTableCell>
                      <DataTableCell>{user.email}</DataTableCell>
                      <DataTableCell style={{ width: 120 }}>
                        <TextField
                          size="small"
                          value={extState.draft}
                          disabled={extState.saving}
                          placeholder="Not set"
                          inputProps={{
                            inputMode: 'numeric',
                            maxLength: EXTENSION_MAX_LENGTH,
                            'aria-label': `3CX extension for ${user.fullname}`,
                          }}
                          sx={{
                            width: 96,
                            '& .MuiOutlinedInput-root': { minHeight: 30 },
                            '& .MuiOutlinedInput-input': { py: '4px' },
                          }}
                          onChange={(e) => handleExtensionChange(user.guid, e.target.value)}
                          onBlur={(e) => handleExtensionCommit(user, e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              (e.target as HTMLInputElement).blur();
                            } else if (e.key === 'Escape') {
                              handleExtensionCancel(user);
                              (e.target as HTMLInputElement).blur();
                            }
                          }}
                        />
                      </DataTableCell>
                      <DataTableCell style={{ minWidth: 360 }}>
                        <Autocomplete
                          multiple
                          size="small"
                          disableCloseOnSelect
                          options={roleOptions}
                          value={state.draft}
                          disabled={state.saving}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              minHeight: 30,
                              py: '1px',
                              gap: '4px',
                            },
                            '& .MuiAutocomplete-tag': { my: '1px' },
                          }}
                          onChange={(_, value) => handleRolesChange(user, value)}
                          renderTags={(value, getTagProps) =>
                            value.map((option, idx) => (
                              <Chip
                                label={option}
                                size="small"
                                {...getTagProps({ index: idx })}
                                key={option}
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              placeholder={state.draft.length === 0 ? 'No roles' : ''}
                            />
                          )}
                        />
                      </DataTableCell>
                      <DataTableCell muted>
                        {user.updatedAt ? formatUpdated(user.updatedAt) : ''}
                      </DataTableCell>
                    </DataTableRow>
                  );
                })
              )}
            </tbody>
          </DataTable>
        </DataTableCard>
      )}

      <Popover
        open={!!colorAnchor}
        anchorEl={colorAnchor?.el ?? null}
        onClose={() => setColorAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 1.5, display: 'grid', gridTemplateColumns: 'repeat(4, 28px)', gap: 1 }}>
          {AVATAR_PALETTE.map((c) => {
            const active = colorAnchor?.user.avatarColor === c.key;
            return (
              <Tooltip key={c.key} title={c.key}>
                <button
                  type="button"
                  onClick={() => colorAnchor && handleColorChange(colorAnchor.user, c.key)}
                  aria-label={c.key}
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: c.bg,
                    color: c.fg,
                    border: '0.5px solid rgba(0,0,0,0.15)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                  }}
                >
                  {active && <IconCheck size={16} stroke={2.5} />}
                </button>
              </Tooltip>
            );
          })}
        </Box>
      </Popover>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
